import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Paper, IconButton, Modal } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Container,
  GameDate,
  GameTitle,
  HeaderContainer,
  HeaderLink,
  StyledChevronRight,
} from './GameDetails.styles';
import formatGameStartAndEndDate from '../games-helpers/formatGameStartAndEndDate';
import { useGetSingleGame } from '../hooks/useGetGames';
import { ErrorContainer, TabsContainer, Tab } from '../Games.styles';
import { Storage } from 'aws-amplify';
import {
  TopThreeAvatar,
  TopThreeName,
  TopThreePosition,
  ViewContainer,
  TopThreeCard,
  TopThreeContainer,
  TopThreePoints,
} from './NormalDetails/NormalDetails.styles';
import {
  transformToTableData,
  getUserAndTeamData,
  enrichIncentiveProgress,
} from './utils';
import Rewards from './NormalDetails/details-components/Rewards';
import GameRulesScreen, {
  Reward,
} from '../../games/GameRules/GameRulesScreen.view';
import Standings from './NormalDetails/details-components/Standings';
import GameDetailsSkeleton from './NormalDetails/details-components/GameDetailsSkeleton';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import CloseIcon from '@mui/icons-material/Close';
import GameCardMenu from 'application/components/GameCardMenu/GameCardMenu';
import { useDeleteGameMutation } from '../hooks/useDeleteGameMutation';
import { GamifyToast } from 'application/common/CustomToasts';
import ConfirmationModal from '../../users/ConfirmationModal';
import NewGameContainer from '../NewGame/NewGame.container';
import DisplayBracketContainer from './DisplayBracket/DisplayBracket.container';
import IncentiveProgress from './Incentive/IncentiveProgress';

interface GameReward {
  id: string;
  name: string;
  description: string;
  imageName: string;
}

interface GameRewardItem {
  reward: GameReward;
  place: number;
  points?: number;
}

enum TabType {
  All = 'all',
  About = 'about',
  Prizes = 'prizes',
}

const TABS = [
  { id: TabType.All, label: 'All' },
  { id: TabType.About, label: 'About' },
  { id: TabType.Prizes, label: 'Prizes' },
];

const GameDetailsView = () => {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState<TabType>(TabType.All);
  const {
    data: game,
    isLoading,
    error,
    refetch,
  } = useGetSingleGame(gameId || '');
  const [gamesTableData, setGamesTableData] = useState<any[]>([]);
  const [gameDataVersion, setGameDataVersion] = useState(0);
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const deleteGameMutation = useDeleteGameMutation();
  const [isRefetching, setIsRefetching] = useState(false);

  // Create a hash of the users data to detect changes
  useEffect(() => {
    // Skip if no game data yet
    if (!game) return;

    // Generate a simple hash based on the users array
    const usersItems = (game as any).users?.items || [];
    const usersCount = usersItems.length;
    const usersHash = usersItems
      .map((user: any) => user.user?.id || 'null')
      .sort()
      .join('|');

    console.log(
      `Game users updated - Count: ${usersCount}, Hash: ${usersHash}`
    );

    // Increment version when users data changes
    setGameDataVersion((prev) => prev + 1);
  }, [(game as any)?.users?.items]);

  // Manually trigger refetch when component mounts
  useEffect(() => {
    let isMounted = true;

    const fetchFreshData = async () => {
      if (!gameId || !isMounted) return;

      setIsRefetching(true);
      try {
        await refetch();
      } catch (error) {
        console.error('Failed to fetch fresh game data:', error);
      } finally {
        if (isMounted) {
          setIsRefetching(false);
        }
      }
    };

    fetchFreshData();

    // Also refetch when window regains focus
    const handleFocus = () => fetchFreshData();
    window.addEventListener('focus', handleFocus);

    return () => {
      isMounted = false;
      window.removeEventListener('focus', handleFocus);
    };
  }, [gameId, refetch]);

  // Separate function for contestant changes vs editing the game
  const handleContestantChangeComplete = useCallback(async () => {
    // Show success message
    GamifyToast.success('Contestant data updated successfully');

    setIsRefetching(true);
    try {
      await refetch();
    } catch (error) {
      console.error('Failed to refresh data after updating points:', error);
    } finally {
      setIsRefetching(false);
    }
  }, [refetch]);

  // Handle closing the edit modal with a redirect - specifically when edit is COMPLETED successfully
  const handleEditComplete = useCallback(() => {
    setShowEditModal(false);

    // Show success message
    GamifyToast.success('Game updated successfully');

    // Short delay before redirect
    setTimeout(() => {
      navigate('/performance/games');
    }, 1500);
  }, [navigate]);

  // Handle just closing the edit modal without saving
  const handleEditModalClose = useCallback(() => {
    setShowEditModal(false);
  }, []);

  useEffect(() => {
    const fetchGamesTableData = async () => {
      // Don't process if still loading
      if (!game) return;

      // Force update on version change or when table is empty
      const shouldForceUpdate =
        gamesTableData.length === 0 || gameDataVersion > 0;

      console.log(
        `Processing table data - Force update: ${shouldForceUpdate}, Version: ${gameDataVersion}, Refetching: ${isRefetching}`
      );

      // Skip processing if we're in the middle of a refetch and have data
      if (isRefetching && gamesTableData.length > 0 && !shouldForceUpdate) {
        console.log('Skipping table update during refetch');
        return;
      }

      try {
        const items = getUserAndTeamData(game);

        // Log the count to debug
        console.log(`Found ${items?.length || 0} contestants in game data`);

        // Always process if we have items, regardless of refetching state
        if (!items || items.length === 0) {
          console.log('No items found in game data');

          // Only clear if we explicitly have no items (not just undefined/loading)
          if (items !== undefined && gamesTableData.length > 0) {
            console.log('Clearing table data as items array is empty');
            setGamesTableData([]);
          }
          return;
        }

        let transformedData = await Promise.all(
          await transformToTableData(items, game.contestType)
        );

        console.log('🚀 ~ transformedData #1: NICK TEST', transformedData);
        // Incentive: enrich rows with per-user KPI progress for drill-down
        if (game.contestType === 'incentive') {
          transformedData = await enrichIncentiveProgress(
            game,
            transformedData
          );
        }
        console.log('🚀 ~ transformedData #2: NICK TEST', transformedData);
        // Only update if we got meaningful data
        if (transformedData && transformedData.length > 0) {
          console.log(`Setting ${transformedData.length} items to table data`);
          setGamesTableData(transformedData);
        }
      } catch (error) {
        console.error('Error processing game data for table:', error);
        // Don't reset existing table data on error
      }
    };

    fetchGamesTableData();
  }, [game, gameDataVersion, isRefetching, gamesTableData.length]);

  useEffect(() => {
    // If game is bracket type and active tab is Prizes, switch to All tab
    if (
      game?.contestType?.includes('bracket') &&
      activeTab === TabType.Prizes
    ) {
      setActiveTab(TabType.All);
    }
  }, [game?.contestType, activeTab]);

  useEffect(() => {
    if (game?.rewards?.items) {
      const fetchRewardImages = async () => {
        const rewardsWithImages = await Promise.all(
          (game.rewards.items as GameRewardItem[]).map(async (item) => {
            let imageUrl = '';
            try {
              if (item.reward.imageName) {
                const url = await Storage.get(item.reward.imageName);
                imageUrl = typeof url === 'string' ? url : `${url}`;
              }
            } catch (err) {
              console.error('Error fetching reward image:', err);
            }
            return {
              id: item.reward.id,
              name: item.reward.name,
              description: item.reward.description,
              imageName: imageUrl,
              place: item.place,
              points: item.points,
            };
          })
        );
        setRewards(rewardsWithImages);
      };
      fetchRewardImages();
    }
  }, [game?.rewards?.items]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleEdit = () => {
    setShowEditModal(true);
    handleMenuClose();
  };

  const handleDelete = () => {
    setShowDeleteConfirmation(true);
  };

  const handleCloseModal = () => {
    setShowDeleteConfirmation(false);
  };

  const handleConfirmDelete = async () => {
    if (!gameId) return;

    try {
      await deleteGameMutation.mutateAsync({ missionID: gameId });
      GamifyToast.success('Success', 'Game has been deleted successfully');
      handleCloseModal();
      navigate('/performance/games');
    } catch (error) {
      console.error('Failed to delete game:', error);
      GamifyToast.error('Error', 'Failed to delete game');
    }
  };

  const handlePlayerCardClick = (player: any) => {
    if (game?.scope === 'circle') {
      navigate(`/teams/${player.id}`);
    } else {
      navigate(`/people/about/${player.id}`);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ bgcolor: theme.custom.background2, minHeight: '100vh' }}>
        <Container>
          <Paper
            sx={{
              bgcolor: theme.custom.background1,
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
              maxWidth: '700px',
              margin: '0 auto',
              minHeight: '120vh',
              position: 'relative',
            }}
          >
            <GameDetailsSkeleton />
          </Paper>
        </Container>
      </Box>
    );
  }

  if (error || !game) {
    return (
      <ErrorContainer>
        Something went wrong fetching the game. Please try again later.
      </ErrorContainer>
    );
  }

  const isIncentiveGame = game.contestType === 'incentive';

  const calculateIncentiveResult = (missions: any[] = []) => {
    if (!Array.isArray(missions) || missions.length === 0) {
      return { result: '0/0', hasReachedGoal: false, completed: 0, total: 0 };
    }

    let completed = 0;
    missions.forEach((mission) => {
      const actual = Number(
        mission?.actual ?? mission?.doneActionCount ?? mission?.points ?? 0
      );
      const goal = Number(
        mission?.goal ?? mission?.goalActionCount ?? mission?.target ?? 0
      );
      if (goal > 0 && actual >= goal) {
        completed += 1;
      }
    });

    const result = `${completed}/${missions.length}`;
    const hasReachedGoal = completed >= missions.length && missions.length > 0;
    return { result, hasReachedGoal, completed, total: missions.length };
  };

  const getTopThreeDisplayValue = (player: any) => {
    if (isIncentiveGame) {
      const missions = Array.isArray(player?.missions) ? player.missions : [];
      const { result } = calculateIncentiveResult(missions);
      return result;
    }

    const value = player?.points ?? player?.score ?? 0;
    return `${value}pts`;
  };

  const renderPlayerCard = (
    player: any,
    position: string,
    isLarge: boolean
  ) => {
    const getInitials = (name: string) => {
      return name
        .split(' ')
        .map((part) => part[0])
        .join('')
        .toUpperCase();
    };

    return (
      <TopThreeCard
        key={player.id}
        isLarge={isLarge}
        onClick={() => handlePlayerCardClick(player)}
        style={{ cursor: 'pointer' }}
      >
        <TopThreeAvatar isLarge={isLarge}>
          {player.profilePicture ? (
            <img src={player.profilePicture} alt={player.name} />
          ) : (
            <div
              style={{
                width: '100%',
                height: '100%',
                backgroundColor: theme.custom.background3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: isLarge ? '32px' : '24px',
                color: theme.custom.textLight,
                fontWeight: '500',
              }}
            >
              {getInitials(player.name)}
            </div>
          )}
        </TopThreeAvatar>
        <TopThreePosition>{position}</TopThreePosition>
        <TopThreeName>{player.name}</TopThreeName>
        <TopThreePoints>{getTopThreeDisplayValue(player)}</TopThreePoints>
      </TopThreeCard>
    );
  };

  const renderTopThree = () => {
    if (!gamesTableData || gamesTableData.length < 3) return null;

    const sortedData = isIncentiveGame
      ? [...gamesTableData].sort((a, b) => {
          const aMetrics = calculateIncentiveResult(a?.missions || []);
          const bMetrics = calculateIncentiveResult(b?.missions || []);

          if (bMetrics.completed !== aMetrics.completed) {
            return bMetrics.completed - aMetrics.completed;
          }

          const aRatio =
            aMetrics.total > 0 ? aMetrics.completed / aMetrics.total : 0;
          const bRatio =
            bMetrics.total > 0 ? bMetrics.completed / bMetrics.total : 0;
          if (bRatio !== aRatio) {
            return bRatio - aRatio;
          }

          const aScore = Number(a?.score ?? 0);
          const bScore = Number(b?.score ?? 0);
          if (bScore !== aScore) {
            return bScore - aScore;
          }

          const aPosition = Number(a?.position ?? Infinity);
          const bPosition = Number(b?.position ?? Infinity);
          return aPosition - bPosition;
        })
      : gamesTableData;

    const topThree = sortedData.slice(0, 3);
    const firstPlayer = topThree?.[0];
    const secondPlayer = topThree?.[1];
    const thirdPlayer = topThree?.[2];

    return (
      <TopThreeContainer>
        {secondPlayer && renderPlayerCard(secondPlayer, '2nd', false)}
        {firstPlayer && renderPlayerCard(firstPlayer, '1st', true)}
        {thirdPlayer && renderPlayerCard(thirdPlayer, '3rd', false)}
      </TopThreeContainer>
    );
  };

  const renderGameInfo = () => (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
        <Box sx={{ width: '100%', textAlign: 'center' }}>
          <GameTitle>{game.name}</GameTitle>
          <GameDate>
            {formatGameStartAndEndDate({
              startDate: new Date(game.startDate),
              endDate: new Date(game.endDate),
            })}
          </GameDate>
        </Box>
        <Box sx={{ position: 'absolute', right: 0, display: 'flex', gap: 1 }}>
          <IconButton onClick={handleMenuClick} size="small">
            <MoreHorizIcon sx={{ color: theme.custom.text }} />
          </IconButton>
          <IconButton onClick={() => navigate(-1)} size="small">
            <CloseIcon sx={{ color: theme.custom.text }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );

  const renderAllTab = () => {
    const isBracketGame =
      game.contestType && game.contestType.includes('bracket');

    if (isBracketGame) {
      return (
        <ViewContainer>
          <DisplayBracketContainer game={game} />
        </ViewContainer>
      );
    }

    return (
      <ViewContainer>
        <Standings
          game={game}
          gamesTableData={gamesTableData}
          refetchData={handleContestantChangeComplete}
        />
        {isIncentiveGame && <IncentiveProgress game={game} />}
      </ViewContainer>
    );
  };

  const renderAboutTab = () => {
    return <GameRulesScreen game={game} rewards={rewards} />;
  };

  const renderPrizesTab = () => {
    const isPointsGame = game.rewardID === 'points';
    return (
      <Box sx={{ p: 2 }}>
        <Rewards rewards={rewards} isPointsGame={isPointsGame} />
      </Box>
    );
  };

  const renderContent = () => {
    const isBracketGame =
      game.contestType && game.contestType.includes('bracket');

    // Don't render Prizes tab content for bracket games
    if (activeTab === TabType.Prizes && isBracketGame) {
      return null;
    }

    switch (activeTab) {
      case TabType.All:
        return renderAllTab();
      case TabType.About:
        return renderAboutTab();
      case TabType.Prizes:
        return renderPrizesTab();
      default:
        return null;
    }
  };

  const renderHeader = () => (
    <HeaderContainer>
      <HeaderLink onClick={() => navigate('/performance')}>
        Performance
      </HeaderLink>
      <StyledChevronRight />
      <HeaderLink onClick={() => navigate('/performance/games')}>
        Games
      </HeaderLink>
      <StyledChevronRight />
    </HeaderContainer>
  );

  return (
    <Box sx={{ bgcolor: theme.custom.background2, minHeight: '100vh' }}>
      {renderHeader()}
      <Container>
        <Paper
          sx={{
            bgcolor: theme.custom.background1,
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
            maxWidth: '700px',
            margin: '0 auto',
            minHeight: '120vh',
            position: 'relative',
          }}
        >
          {renderGameInfo()}
          {renderTopThree()}
          <TabsContainer>
            {TABS.filter((tab) => {
              // Hide the Prizes tab for bracket games
              if (
                tab.id === TabType.Prizes &&
                game.contestType &&
                game.contestType.includes('bracket')
              ) {
                return false;
              }
              return true;
            }).map((tab) => (
              <Tab
                key={tab.id}
                isActive={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </Tab>
            ))}
          </TabsContainer>
          {renderContent()}
        </Paper>
      </Container>

      {/* Edit Game Modal */}
      <Modal
        open={showEditModal}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick') {
            handleEditModalClose();
          }
        }}
        aria-labelledby="edit-game-modal"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}
      >
        <div onClick={(e) => e.stopPropagation()}>
          <NewGameContainer
            onClose={handleEditModalClose}
            gameId={gameId}
            onSaveComplete={handleEditComplete}
          />
        </div>
      </Modal>

      <GameCardMenu
        anchorEl={menuAnchorEl}
        onClose={handleMenuClose}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <ConfirmationModal
        open={showDeleteConfirmation}
        onClose={handleCloseModal}
        title="Delete Game"
        message={
          game
            ? `Are you sure you want to delete "${game.name}"? This action cannot be undone.`
            : ''
        }
        confirmationButtonText="Delete"
        onConfirm={handleConfirmDelete}
        isLoading={deleteGameMutation.isPending}
      />
    </Box>
  );
};

export default GameDetailsView;
